import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth0Data } from '../../context/Auth0DataContext';
import apiClient from '../../lib/api';
import AccountDetailModal from './AccountDetailModal';
import AddAccountModal from './AddAccountModal';
import { STAGES } from './constants';

const { FiPlus, FiSearch, FiRefreshCw, FiUser, FiExternalLink, FiZap, FiX } = FiIcons;

// Companies-in-pipeline board. Accounts arrive from the sourcing agent (or manual
// entry); reps claim/note/stage them here. Drag a card between columns to restage.
const CRM = () => {
  const { user, getAllUsers } = useAuth0Data();
  const [accounts, setAccounts] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [actionError, setActionError] = useState(null);

  const loadAccounts = useCallback(async () => {
    try {
      setError(null);
      const data = await apiClient.getCrmAccounts();
      setAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load CRM accounts:', err);
      setError(err.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const list = (await getAllUsers?.()) || [];
      setUsersById(Object.fromEntries(list.map((u) => [u.id, u])));
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }, [getAllUsers]);

  useEffect(() => {
    loadAccounts();
    loadUsers();
    // Reload when the active team changes (TeamContext dispatches this).
    const onTeamSwitch = () => { setLoading(true); loadAccounts(); loadUsers(); };
    window.addEventListener('team-switched', onTeamSwitch);
    return () => window.removeEventListener('team-switched', onTeamSwitch);
  }, [loadAccounts, loadUsers]);

  const ownerName = useCallback(
    (id) => (id ? usersById[id]?.name || usersById[id]?.email || 'Unknown' : null),
    [usersById]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return accounts;
    const q = search.toLowerCase();
    return accounts.filter((a) => a.company?.toLowerCase().includes(q));
  }, [accounts, search]);

  const byStage = useMemo(() => {
    const groups = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    for (const a of filtered) (groups[a.stage] || (groups[a.stage] = [])).push(a);
    return groups;
  }, [filtered]);

  // Optimistic restage on drop.
  const moveToStage = async (account, stage) => {
    if (!account || account.stage === stage) return;
    const prev = account.stage;
    setActionError(null);
    setAccounts((cur) => cur.map((a) => (a.id === account.id ? { ...a, stage } : a)));
    try {
      await apiClient.updateCrmAccount(account.id, { stage });
    } catch (err) {
      console.error('Failed to restage:', err);
      setAccounts((cur) => cur.map((a) => (a.id === account.id ? { ...a, stage: prev } : a)));
      setActionError(
        `Couldn't move "${account.company}" to ${stage}: ${err.message || 'request failed'}`
      );
    }
  };

  const handleDrop = (e, stage) => {
    e.preventDefault();
    setDragOverStage(null);
    const id = e.dataTransfer.getData('text/plain');
    const account = accounts.find((a) => a.id === id);
    moveToStage(account, stage);
  };

  const fmtAmount = (amt) => {
    if (amt === null || amt === undefined || amt === '') return null;
    const n = Number(amt);
    if (Number.isNaN(n)) return null;
    if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${n}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">CRM Pipeline</h1>
            <p className="text-gray-600">Companies being pursued — sourced by the agent or added by hand</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search company…"
                className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={() => { setLoading(true); loadAccounts(); }}
              className="p-2 text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg"
              title="Refresh"
            >
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
              Add Company
            </button>
          </div>
        </div>
      </motion.div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start justify-between gap-3">
          <span>{actionError}</span>
          <button
            onClick={() => setActionError(null)}
            className="text-red-500 hover:text-red-800 flex-shrink-0"
            aria-label="Dismiss"
          >
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Board */}
      {loading ? (
        <div className="text-center text-gray-500 py-20">Loading pipeline…</div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STAGES.map((stage) => {
            const cards = byStage[stage.key] || [];
            return (
              <div
                key={stage.key}
                onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage.key); }}
                onDragLeave={() => setDragOverStage((s) => (s === stage.key ? null : s))}
                onDrop={(e) => handleDrop(e, stage.key)}
                className={`flex-shrink-0 w-72 rounded-xl border p-3 transition-colors ${
                  dragOverStage === stage.key ? 'border-blue-400 bg-blue-50' : 'border-gray-100 bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${stage.dot}`} />
                    <h3 className="font-semibold text-gray-800 text-sm">{stage.label}</h3>
                  </div>
                  <span className="text-xs text-gray-500 bg-white border border-gray-200 rounded-full px-2 py-0.5">
                    {cards.length}
                  </span>
                </div>

                <div className="space-y-2 min-h-[60px]">
                  {cards.map((a) => {
                    const amt = fmtAmount(a.amount);
                    const owner = ownerName(a.ownerUserId);
                    return (
                      <motion.div
                        layout
                        key={a.id}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('text/plain', a.id)}
                        onClick={() => setDetailId(a.id)}
                        className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-gray-900 text-sm leading-tight">{a.company}</p>
                          {a.coverageUrl && (
                            <a
                              href={a.coverageUrl}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-gray-400 hover:text-blue-600 flex-shrink-0"
                            >
                              <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          {amt && (
                            <span className="text-xs font-medium text-green-700 bg-green-50 rounded px-1.5 py-0.5">
                              {amt}
                            </span>
                          )}
                          {a.series && (
                            <span className="text-xs text-gray-600 bg-gray-100 rounded px-1.5 py-0.5">
                              {a.series}
                            </span>
                          )}
                          {a.source && a.source !== 'manual' && (
                            <span className="text-xs text-purple-700 bg-purple-50 rounded px-1.5 py-0.5 flex items-center gap-0.5">
                              <SafeIcon icon={FiZap} className="w-3 h-3" />
                              {a.source}
                            </span>
                          )}
                        </div>

                        {a.lastNote && (
                          <p className="text-xs text-gray-500 mt-2 line-clamp-2">{a.lastNote}</p>
                        )}

                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                          <span className="text-xs flex items-center gap-1 text-gray-500">
                            <SafeIcon icon={FiUser} className="w-3 h-3" />
                            {owner || <span className="italic text-gray-400">Unclaimed</span>}
                          </span>
                          {a.bullhornStatus && a.bullhornStatus !== 'not_in_bullhorn' && (
                            <span className="text-xs text-amber-700">🗂️ Bullhorn</span>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <AddAccountModal
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); loadAccounts(); }}
        />
      )}

      {detailId && (
        <AccountDetailModal
          accountId={detailId}
          currentUserId={user?.sub}
          usersById={usersById}
          onClose={() => setDetailId(null)}
          onChanged={loadAccounts}
        />
      )}
    </div>
  );
};

export default CRM;
